import React from "react";
import { Drawer, Divider, Empty, Spin } from "antd";
import { useOrderByIdQuery } from "../../Services";
import { AuditLogEntry } from "./AuditLogEntry";
import { getOrderHistory } from "./historyUtils";
import { LegacyHistoryPanel } from "./Legacyhistorypanel";
import { drawerTitle } from "../AppUi";

interface OrderHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  orderId: string | null;
}

const EmptyHistory = () => (
  <Empty
    image={Empty.PRESENTED_IMAGE_SIMPLE}
    description={
      <span className="text-gray-400 text-xs">
        No edit history recorded for this order.
      </span>
    }
  />
);

export const OrderHistoryDrawer: React.FC<OrderHistoryDrawerProps> = ({
  open,
  onClose,
  orderId,
}) => {
  const { data: order, isFetching: loading } = useOrderByIdQuery(
    orderId || "",
    { enabled: open && !!orderId },
  );

  const {
    auditLogs,
    legacyItems,
    legacyPayments,
    hasAuditLogs,
    hasLegacyLogs,
    hasHistory,
  } = getOrderHistory(order);

  return (
    <Drawer
      title={drawerTitle(
        `Edit & Audit History${order?.order_no ? ` - ${order.order_no}` : ""}`,
      )}
      open={open}
      onClose={onClose}
      width={560}
      placement="right"
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center h-64 gap-2">
          <Spin size="default" />
          <span className="text-xs text-gray-400">
            Fetching history details...
          </span>
        </div>
      ) : !hasHistory ? (
        <EmptyHistory />
      ) : (
        <div className="flex flex-col gap-3">
          {hasAuditLogs && (
            <>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Audit Log
              </div>
              {auditLogs.map((log) => (
                <AuditLogEntry key={log.id} log={log} />
              ))}
            </>
          )}

          {hasLegacyLogs && (
            <>
              {hasAuditLogs && <Divider className="my-1" />}
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Legacy Edit &amp; Deletion History
              </div>
              <LegacyHistoryPanel
                historyItems={legacyItems}
                historyPayments={legacyPayments}
              />
            </>
          )}
        </div>
      )}
    </Drawer>
  );
};
